import { NavigationHistoryStack } from 'ui-ng-navbar';
import * as _4 from 'lodash4';
import { ApplicationDetails, ApplicationConfiguration } from '../../models/app_config_model';
import { AccountModel } from 'data-ng-authentication';

type TTrackingRecord = Record<string, any>;

// tslint:disable-next-line:cognitive-complexity
export function Routing(
  $rootScope: ng.IRootScopeService,
  $stickyState: any,
  $deepStateRedirect: any,
  ConsistentPageConfiguration: any,
  featureConfigAdapter: any,
  log: any,
  $timeout: ng.ITimeoutService,
  DefaultRoute: string,
  appDetails: ApplicationDetails,
  accountModel: AccountModel,
  appConfigModel: ApplicationConfiguration,
  ngNavigationHistoryStack: NavigationHistoryStack
) {
  ngNavigationHistoryStack.defaultStateName = DefaultRoute;

  // check for hosted eab domain and not localdev
  const isEabHostedDomain = _4.includes(location.hostname, 'eab.com');

  const focusOnHeader = () => {
    angular.element('#appHeader').attr('tabindex', '-1').focus().removeAttr('tabindex');
  };

  /**
   * @returns true if a string partially exists in an array
   */
  const hasGroupOf = (groups: string[], s: string) => groups.join(' ').indexOf(s) >= 0;
  /**
   * @returns true if a string partially exists in a uri (or any string)
   */
  const inURL = (url: string, s: string) => url.indexOf(s) >= 0;

  const createUserTracking = (): TTrackingRecord => {
    // map group_names to *_user_types
    //
    const groupNames = accountModel.groupNames || [];
    const hasGroupOther =
      groupNames.find((_: string) => !hasGroupOf(groupNames, 'student') && !hasGroupOf(groupNames, 'advisor')) !== null;
    const userTypes = [
      hasGroupOf(groupNames, 'student') ? 'Student' : undefined,
      hasGroupOf(groupNames, 'advisor') ? 'Advisor' : undefined,
      hasGroupOther ? 'Content Admin' : undefined
    ]
      .filter((_) => _)
      .join(',');

    const visitor__gainSightPX = {
      id: accountModel.gainsight_id || accountModel.tracking_id,
      sgd_first_name: accountModel.firstName,
      sgd_last_name: accountModel.lastName,
      sgd_email: accountModel.email,
      sgd_user_types: userTypes,
      sgd_classification: `${accountModel.yearClassification}`,
      language: 'en_US',
      sgd_device: 'Desktop'
    };

    // add an `id` to the visitor if the visitor is not FAKE, SuperUser, or Guest
    //
    const visitorID = accountModel.gainsight_id || accountModel.tracking_id;
    const isFakeStudent = visitorID.indexOf('FAKE') >= 0;
    const isSuperUser = accountModel.isSuperuser;
    // TODO is not `is_guest`
    if (!isFakeStudent || !isSuperUser) {
      visitor__gainSightPX.id = accountModel.gainsight_id;
    }

    return visitor__gainSightPX;
  };

  const createAccountTracking = (): TTrackingRecord => {
    const url = location.hostname.toLowerCase();

    // compute actual environment and not built environment
    //
    const sgd_environment = inURL(url, 'impl')
      ? 'Impl'
      : inURL(url, 'dev') || url.match(/\d+\.\d+\.\d+\.\d+/) !== null
      ? 'Dev'
      : inURL(url, 'sandbox') || inURL(url, 'demo') || inURL(url, 'realtime')
      ? 'Other'
      : 'Prod';

    return {
      id: accountModel.account_name,
      sgd_product: 'Smart Guidance',
      name: (appConfigModel.model.member_configs || {})['core-inst_name'],
      sgd_has_ap: appConfigModel.isFeatureEnabled('ap3-academic_planner_v3'),
      sgd_has_registration: appConfigModel.isFeatureEnabled('reg-scheduler'),
      sgd_environment
    };
  };

  // TODO wrap this in _4.once
  const initializeGainSightPX = (user: TTrackingRecord, account: TTrackingRecord) => {
    const hasAptrinsic = 'aptrinsic' in window && _4.isFunction(window['aptrinsic']);
    if (isEabHostedDomain && hasAptrinsic) {
      window['aptrinsic']('identify', user, account);
      window['aptrinsic']('set', 'globalContext', { Device: user['Device'] });
    }
  };

  ConsistentPageConfiguration.activate();

  // DEBT leave alone, this will be fixed in a broader UI solution for error handling
  $rootScope.$on('$stateChangeError', (event, toState, toParams, fromState, fromParams, error) => {
    log.view('state-error', {
      screenName: toState,
      error
    });
    log.error(error);
  });

  // DEBT leave alone, this will be fixed in a broader UI solution for error handling
  $rootScope.$on('$stateNotFound', (event, unfoundState, fromState, fromParams) => {
    log.issue('The page you attempted to navigate to was not found');
    log.view('state-not-found', {
      screenName: unfoundState
    });
  });

  $rootScope.$on('$stateChangeSuccess', (event, toState, toParams, fromState, fromParams, error) => {
    const state = {
      name: fromState.name,
      params: fromParams,
      data: fromState.data
    };

    if (accountModel.isLoggedIn()) {
      initializeGainSightPX(createUserTracking(), createAccountTracking());
    }

    ngNavigationHistoryStack.changeNavigatioHistoryStack(state, toState);

    $rootScope.requestedTransitionDirection = $rootScope.transitionDirection;
    $rootScope.previousState = fromState;
    $rootScope.previousState['params'] = fromParams;

    $('body')
      .prop('class', '')
      .addClass('page ' + toState.name.split('.').join(' '));

    log.view('state-start', {
      screenName: toState.name
    });
    /*
     * Resets the focus on every page navigation unless specified in `preventResetFocus`
     */
    if (!_4.get(toState, 'data.preventResetFocus', false)) {
      $timeout(() => {
        focusOnHeader();
      }, 100);
    }
    // document.activeElement.nodeName === 'BODY'
    /*
      DSR is used only with `my.routeModal` route
    */
    const isRouteModalState = _4.includes(toState, 'my.routeModal');
    if (isRouteModalState) {
      $stickyState.reset('*');
      $deepStateRedirect.reset(fromState.name);
    }
  });

  $rootScope.$on('$stateChangeStart', (event, toState, toParams, fromState, fromParams) => {
    ngNavigationHistoryStack.changeStateStatus(fromState, toState);

    if (toParams.path === fromParams.path && fromState.name === 'my.fp.active' && toState.name === 'my.fp.active') {
      toParams.path = '';
    }

    $('body').removeClass('modal-activate');
    const openModal = angular.element('body').find(`[role='dialog']`);
    if (openModal.length > 0 && openModal.scope() && _4.isFunction(openModal.scope().$hide)) {
      openModal.scope().$hide();
    }
  });

  featureConfigAdapter.enableFeatureStateBlocking();
}



// WEBPACK FOOTER //
// ./app/lib/core/configs/routing.ts